import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Loader2, TrendingUp, TrendingDown, Minus, DollarSign, Target, AlertTriangle, Download, FileText, Activity, Zap, Users, Database } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import AdminNavigation from '@/components/AdminNavigation';
import AdminFooter from '@/components/AdminFooter';

interface PricingProduct {
  name: string;
  currentPrice: number;
  costPerCustomer: number;
  margin: number;
  marginPercent: number;
  recommendation: 'increase' | 'decrease' | 'maintain';
  suggestedPrice?: number;
}

interface PricingComparison {
  products: PricingProduct[];
  summary: {
    totalProducts: number;
    averageMargin: number;
    productsNeedingAdjustment: number;
    potentialRevenueImpact: number;
  };
}

// Real-time telemetry data structure
interface TelemetryData {
  totalCost24h: number;
  costByType: {
    openai_tokens: number;
    openai_images: number;
    openai_tts: number;
    storage_gb: number;
    server_compute: number;
    email_sends: number;
  };
  topUsers: Array<{
    userId: number;
    cost: number;
  }>;
  totalStories: number;
  totalImages: number;
}

// Custom hook for real-time telemetry
function useRealTimeTelemetry() {
  const [data, setData] = useState<TelemetryData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check if user is admin before attempting SSE connection
  const { data: userResponse } = useQuery<{user: {isAdmin: boolean}}>({
    queryKey: ['/api/user'],
    retry: 1
  });

  const isAdmin = userResponse?.user?.isAdmin;

  useEffect(() => {
    // Only connect if user is authenticated AND is an admin
    if (!isAdmin) {
      console.log('User is not admin, skipping SSE connection');
      return;
    }

    const connectSSE = async () => {
      try {
        // Create abort controller for this connection
        const controller = new AbortController();
        abortControllerRef.current = controller;

        console.log('Connecting to SSE with credentials...');
        
        // Use fetch with credentials instead of EventSource to ensure cookies are sent
        const response = await fetch('/api/telemetry/stream', {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          credentials: 'include', // This ensures cookies are sent
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
        }

        console.log('SSE connection established successfully');
        setIsConnected(true);
        
        // Read the streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error('Response body is not readable');
        }

        // Process the stream
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          if (controller.signal.aborted) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.slice(6).trim();
                if (jsonData) {
                  const message = JSON.parse(jsonData);
                  
                  if (message.type === 'cost_update' && message.data) {
                    setData(message.data);
                  }
                  
                  console.log('Received SSE message:', message.type);
                }
              } catch (error) {
                console.error('Error parsing SSE message:', error);
              }
            }
          }
        }

      } catch (error) {
        console.error('SSE connection error:', error);
        setIsConnected(false);
        
        // Only attempt to reconnect if not aborted (component still mounted)
        if (!abortControllerRef.current?.signal.aborted) {
          console.log('Reconnecting SSE in 5 seconds...');
          setTimeout(connectSSE, 5000);
        }
      }
    };

    connectSSE();

    return () => {
      // Clean up: abort the fetch request
      if (abortControllerRef.current) {
        console.log('Closing SSE connection');
        abortControllerRef.current.abort();
      }
    };
  }, [isAdmin]);

  return { data, isConnected };
}

export default function AdminPricingIntelligence() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showRecommendations, setShowRecommendations] = useState(false);
  const { data: telemetryData, isConnected: telemetryConnected } = useRealTimeTelemetry();
  
  const { data: comparison, isLoading, error } = useQuery<{data: PricingComparison}>({
    queryKey: ['/api/admin/pricing/comparison'],
    retry: 1
  });

  const initializeMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/pricing/initialize'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/pricing/comparison'] });
    }
  });

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'increase': return <TrendingUp className="h-4 w-4" />;
      case 'decrease': return <TrendingDown className="h-4 w-4" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'increase': return 'destructive';
      case 'decrease': return 'secondary'; 
      default: return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent.toFixed(1)}%`;
  };

  const exportReport = () => {
    if (!pricingData) return;
    
    // Generate CSV content
    const headers = ['Product Name', 'Current Price', 'Cost per Customer', 'Margin', 'Margin %', 'Recommendation', 'Suggested Price'];
    const csvContent = [
      headers.join(','),
      ...pricingData.products.map(product => [
        `"${product.name}"`,
        product.currentPrice,
        product.costPerCustomer,
        product.margin,
        product.marginPercent.toFixed(1),
        product.recommendation,
        product.suggestedPrice || ''
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pricing-intelligence-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Report Exported",
      description: "Pricing intelligence report has been downloaded as CSV",
    });
  };

  const openRecommendations = () => {
    setShowRecommendations(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load pricing intelligence data. 
            <Button 
              onClick={() => initializeMutation.mutate()}
              disabled={initializeMutation.isPending}
              className="ml-2"
              size="sm"
            >
              {initializeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Initializing...
                </>
              ) : (
                'Initialize System'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const pricingData = comparison?.data || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50" data-testid="admin-pricing-intelligence">
      <AdminNavigation currentPage="Pricing Intelligence" showBackButton={true} />
      
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
          <div>
            <p className="text-sm md:text-base text-gray-600">Analyze current pricing vs costs and review recommendations</p>
          </div>
          <Button 
            onClick={() => initializeMutation.mutate()}
            disabled={initializeMutation.isPending}
            variant="outline"
            className="w-full md:w-auto"
          >
            {initializeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Refreshing...
              </>
            ) : (
              'Refresh Data'
            )}
          </Button>
        </div>

        {/* Real-time Cost Monitoring Dashboard */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" data-testid="realtime-cost-dashboard">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                  <Activity className="h-5 w-5 text-purple-600" />
                  Real-time Cost Monitoring
                </CardTitle>
                <CardDescription>
                  Live operational costs and resource usage (last 24 hours)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${telemetryConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-muted-foreground">
                  {telemetryConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {telemetryData ? (
              <div className="space-y-6">
                {/* Cost Overview */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-600">Total Costs (24h)</p>
                        <p className="text-2xl font-bold text-blue-900">
                          ${telemetryData.totalCost24h.toFixed(2)}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-blue-500" />
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-600">Stories Generated</p>
                        <p className="text-2xl font-bold text-green-900">{telemetryData.totalStories}</p>
                      </div>
                      <FileText className="h-8 w-8 text-green-500" />
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-purple-600">Images Generated</p>
                        <p className="text-2xl font-bold text-purple-900">{telemetryData.totalImages}</p>
                      </div>
                      <Zap className="h-8 w-8 text-purple-500" />
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-orange-600">Active Users</p>
                        <p className="text-2xl font-bold text-orange-900">{telemetryData.topUsers.length}</p>
                      </div>
                      <Users className="h-8 w-8 text-orange-500" />
                    </div>
                  </div>
                </div>

                {/* Cost Breakdown */}
                <div>
                  <h4 className="text-sm font-semibold mb-3">Cost Breakdown by Service</h4>
                  <div className="space-y-3">
                    {Object.entries(telemetryData.costByType).map(([service, cost]) => {
                      const percentage = telemetryData.totalCost24h > 0 
                        ? (cost / telemetryData.totalCost24h) * 100 
                        : 0;
                      
                      return (
                        <div key={service} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="capitalize font-medium">
                              {service.replace(/_/g, ' ')}
                            </span>
                            <span>${cost.toFixed(2)} ({percentage.toFixed(1)}%)</span>
                          </div>
                          <Progress value={percentage} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Top Cost Users */}
                {telemetryData.topUsers.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-3">Top Cost Users</h4>
                    <div className="space-y-2">
                      {telemetryData.topUsers.slice(0, 5).map((user, index) => (
                        <div key={user.userId} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded">
                          <span className="text-sm font-medium">User #{user.userId}</span>
                          <span className="text-sm text-gray-600">${user.cost.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Connecting to real-time telemetry...</p>
              </div>
            )}
          </CardContent>
        </Card>

      {pricingData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200" data-testid="summary-total-products">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <DollarSign className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{pricingData.summary.totalProducts}</div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200" data-testid="summary-average-margin">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Target className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle className="text-sm font-medium">Average Margin</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{formatPercent(pricingData.summary.averageMargin)}</div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200" data-testid="summary-needs-adjustment">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                    <AlertTriangle className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle className="text-sm font-medium">Need Adjustment</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{pricingData.summary.productsNeedingAdjustment}</div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-shadow duration-200" data-testid="summary-revenue-impact">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <TrendingUp className="h-4 w-4 text-white" />
                  </div>
                  <CardTitle className="text-sm font-medium">Revenue Impact</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">{formatCurrency(pricingData.summary.potentialRevenueImpact)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Products Analysis */}
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">Product Pricing Analysis</CardTitle>
              <CardDescription>
                Current pricing compared to service costs with recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pricingData.products.map((product: PricingProduct, index: number) => (
                  <div 
                    key={index} 
                    className="border rounded-lg p-3 md:p-4 space-y-3"
                    data-testid={`product-analysis-${index}`}
                  >
                    <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:justify-between md:items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-base md:text-lg">{product.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={getRecommendationColor(product.recommendation)} className="text-xs">
                            {getRecommendationIcon(product.recommendation)}
                            <span className="ml-1 capitalize">{product.recommendation}</span>
                          </Badge>
                        </div>
                      </div>
                      <div className="text-left md:text-right">
                        <div className="text-xl md:text-2xl font-bold">{formatCurrency(product.currentPrice)}</div>
                        <div className="text-sm text-gray-500">Current Price</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-sm">
                      <div data-testid={`cost-per-customer-${index}`}>
                        <div className="text-gray-500">Cost per Customer</div>
                        <div className="font-semibold">{formatCurrency(product.costPerCustomer)}</div>
                      </div>
                      <div data-testid={`margin-${index}`}>
                        <div className="text-gray-500">Margin</div>
                        <div className="font-semibold">{formatCurrency(product.margin)}</div>
                      </div>
                      <div data-testid={`margin-percent-${index}`}>
                        <div className="text-gray-500">Margin %</div>
                        <div className={`font-semibold ${
                          product.marginPercent < 20 ? 'text-red-600' : 
                          product.marginPercent > 80 ? 'text-orange-600' : 'text-green-600'
                        }`}>
                          {formatPercent(product.marginPercent)}
                        </div>
                      </div>
                      {product.suggestedPrice && (
                        <div data-testid={`suggested-price-${index}`}>
                          <div className="text-gray-500">Suggested Price</div>
                          <div className="font-semibold text-blue-600">{formatCurrency(product.suggestedPrice)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:justify-center md:space-x-4">
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full md:w-auto"
              onClick={exportReport}
              data-testid="button-export-report"
            >
              <Download className="mr-2 h-4 w-4" />
              Export Report
            </Button>
            <Dialog open={showRecommendations} onOpenChange={setShowRecommendations}>
              <DialogTrigger asChild>
                <Button 
                  size="lg" 
                  disabled={pricingData.summary.productsNeedingAdjustment === 0} 
                  className="w-full md:w-auto"
                  onClick={openRecommendations}
                  data-testid="button-review-recommendations"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Review Recommendations
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Pricing Recommendations</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Products to Increase</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                          {pricingData.products.filter(p => p.recommendation === 'increase').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Products to Decrease</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-orange-600">
                          {pricingData.products.filter(p => p.recommendation === 'decrease').length}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Products to Maintain</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {pricingData.products.filter(p => p.recommendation === 'maintain').length}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div className="space-y-4">
                    {pricingData.products
                      .filter(product => product.recommendation !== 'maintain')
                      .map((product, index) => (
                        <Card key={index} className="border-l-4 border-l-blue-500">
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <h3 className="font-semibold text-lg">{product.name}</h3>
                                <Badge variant={getRecommendationColor(product.recommendation)} className="mt-1">
                                  {getRecommendationIcon(product.recommendation)}
                                  <span className="ml-1 capitalize">{product.recommendation} Price</span>
                                </Badge>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-500">Current</div>
                                <div className="font-semibold">{formatCurrency(product.currentPrice)}</div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-gray-500">Cost</div>
                                <div className="font-semibold">{formatCurrency(product.costPerCustomer)}</div>
                              </div>
                              <div>
                                <div className="text-gray-500">Current Margin</div>
                                <div className={`font-semibold ${
                                  product.marginPercent < 20 ? 'text-red-600' : 
                                  product.marginPercent > 80 ? 'text-orange-600' : 'text-green-600'
                                }`}>
                                  {formatPercent(product.marginPercent)}
                                </div>
                              </div>
                              {product.suggestedPrice && (
                                <>
                                  <div>
                                    <div className="text-gray-500">Suggested Price</div>
                                    <div className="font-semibold text-blue-600">{formatCurrency(product.suggestedPrice)}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">New Margin</div>
                                    <div className="font-semibold text-green-600">
                                      {formatPercent(((product.suggestedPrice - product.costPerCustomer) / product.suggestedPrice) * 100)}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                              <div className="text-sm font-medium text-blue-800 mb-1">Recommendation Rationale:</div>
                              <div className="text-sm text-blue-700">
                                {product.recommendation === 'increase' && 
                                  `Current margin of ${formatPercent(product.marginPercent)} is below optimal range. Increasing to ${formatCurrency(product.suggestedPrice || 0)} would improve profitability while remaining competitive.`
                                }
                                {product.recommendation === 'decrease' && 
                                  `Current margin of ${formatPercent(product.marginPercent)} is above market standards. Reducing to ${formatCurrency(product.suggestedPrice || 0)} could increase market penetration.`
                                }
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </>
      )}
      </div>
      <AdminFooter />
    </div>
  );
}